import { useEffect, useState } from "react";
import { api } from "../api";
import { formatDate } from "../lib/formatDate";
import type { AiUsageMeter } from "../types";

// Settings → Plan & Billing section.
//
// SINGLE SOURCE OF TRUTH: Stripe, via /api/billing/subscription-
// details. The D1 `users.plan` column can drift (dev data, missed
// webhook, subscription deleted in the Stripe dashboard), so we
// don't trust it to decide whether to show the "Pro" UI. On mount
// we fetch the live Stripe view:
//
//   - 200 + status in {active, trialing, past_due} → Pro branch
//     (with a further split for cancel_at_period_end)
//   - 404 or status in {canceled, incomplete_expired, …}
//       → fall back to D1's trial countdown:
//         - daysRemaining > 0  → trial branch
//         - otherwise          → expired branch
//
// D1-sourced props (plan, daysRemaining, hasSubscription,
// cancelAtPeriodEnd, currentPeriodEnd) remain useful for the
// trial/expired branches (daysRemaining) and for optimistic
// updates after a mutation, but they never override Stripe.

interface Props {
  plan: string;
  daysRemaining: number | null;
  hasSubscription: boolean;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  aiMeter: AiUsageMeter | null;
  // Called after a mutation (cancel / reactivate / switch-plan) so
  // AuthenticatedApp can re-fetch /api/me and the sidebar banners /
  // pickers stay in sync with the new state.
  onBillingChanged?: () => void;
}

type ActionKey =
  | "checkout-monthly"
  | "checkout-annual"
  | "portal"
  | "cancel"
  | "reactivate"
  | "switch";

type Branch = "loading" | "active" | "cancelling" | "trial" | "expired";

interface LiveDetails {
  cadence: "monthly" | "annual" | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  status: string | null;
}

// Stripe statuses we treat as "subscribed and paying" for UI
// purposes. `past_due` keeps the user in the Pro branch so they
// can update their payment method via the portal to recover —
// kicking them to the upgrade screen would be worse UX.
function isStripeLive(status: string | null): boolean {
  return (
    status === "active" || status === "trialing" || status === "past_due"
  );
}

// The billing worker speaks in short error codes (e.g.
// `already_subscribed`, `no_subscription`). Rendering those raw to
// the user is noisy, so every user-facing catch maps through here.
// Unknown / unexpected codes collapse to a single generic string —
// better than leaking "subscription_has_no_items" at the user.
const FRIENDLY_BILLING_ERRORS: Record<string, string> = {
  already_subscribed:
    "You already have an active subscription. Refresh this page to see it.",
  no_stripe_customer: "You don't have a subscription set up yet.",
  no_subscription: "No active subscription to modify.",
  invalid_plan: "Please pick a valid plan.",
  subscription_has_no_items:
    "Your subscription is in an unexpected state — please contact support.",
  already_on_plan: "You're already on that plan.",
};

function friendlyBillingError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  return (
    FRIENDLY_BILLING_ERRORS[msg] ?? "Something went wrong — please try again."
  );
}

export default function SubscriptionSettings({
  daysRemaining,
  cancelAtPeriodEnd,
  currentPeriodEnd,
  aiMeter,
  onBillingChanged,
}: Props) {
  const [busy, setBusy] = useState<ActionKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Live Stripe snapshot. `"loading"` while the first fetch is in
  // flight; `null` when Stripe says there's no subscription; the
  // full payload when there is one. We re-fetch after every
  // mutation so the UI never shows a stale cadence or renewal date.
  const [details, setDetails] = useState<
    "loading" | LiveDetails | null
  >("loading");

  async function refreshDetails() {
    setDetails("loading");
    try {
      const r = await api.billingSubscriptionDetails();
      setDetails(r);
    } catch (err) {
      const msg = (err as Error).message;
      // "no_subscription" is the expected shape for users who never
      // checked out OR whose Stripe sub is gone. Any other error
      // (network, 500) we log and also treat as "no subscription"
      // — better to show the upgrade flow than a confusing "Pro"
      // header followed by buttons that won't work.
      if (msg !== "no_subscription") {
        console.warn("[billing] subscription-details fetch failed", err);
      }
      setDetails(null);
    }
  }

  useEffect(() => {
    void refreshDetails();
    // Run once on mount. The parent component remounts us on settings
    // open via the usual React lifecycle, so this is sufficient.
  }, []);

  // Derive the render branch from Stripe's view first, falling back
  // to the trial countdown only when Stripe says there's nothing
  // active.
  const branch: Branch = (() => {
    if (details === "loading") return "loading";
    if (details && isStripeLive(details.status)) {
      return details.cancel_at_period_end ? "cancelling" : "active";
    }
    // No live Stripe subscription. Trust the D1 trial window.
    if (daysRemaining !== null && daysRemaining > 0) return "trial";
    return "expired";
  })();

  // --- Actions ---

  async function goToCheckout(target: "monthly" | "annual") {
    setBusy(target === "annual" ? "checkout-annual" : "checkout-monthly");
    setError(null);
    try {
      const { url } = await api.billingCheckout(target);
      window.location.href = url;
    } catch (err) {
      setError(friendlyBillingError(err));
      setBusy(null);
    }
  }

  async function goToPortal() {
    setBusy("portal");
    setError(null);
    try {
      const { url } = await api.billingPortal();
      window.location.href = url;
    } catch (err) {
      setError(friendlyBillingError(err));
      setBusy(null);
    }
  }

  async function doCancel() {
    setBusy("cancel");
    setError(null);
    try {
      await api.billingCancel();
      await refreshDetails();
      setConfirmingCancel(false);
      onBillingChanged?.();
    } catch (err) {
      setError(friendlyBillingError(err));
    } finally {
      setBusy(null);
    }
  }

  async function doReactivate() {
    setBusy("reactivate");
    setError(null);
    try {
      await api.billingReactivate();
      await refreshDetails();
      onBillingChanged?.();
    } catch (err) {
      setError(friendlyBillingError(err));
    } finally {
      setBusy(null);
    }
  }

  async function doSwitchPlan() {
    if (!details || details === "loading" || !details.cadence) return;
    const target: "monthly" | "annual" =
      details.cadence === "monthly" ? "annual" : "monthly";
    setBusy("switch");
    setError(null);
    try {
      await api.billingSwitchPlan(target);
      await refreshDetails();
      onBillingChanged?.();
    } catch (err) {
      setError(friendlyBillingError(err));
    } finally {
      setBusy(null);
    }
  }

  // Two-step cancel confirmation — no native confirm() dialog.
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  // --- Render helpers ---

  // When the Stripe fetch has returned, pull cadence + period-end
  // from there. Optimistic D1 currentPeriodEnd is only used to
  // bridge the moment AFTER a mutation while the fresh details
  // fetch is in flight.
  const liveCadence = details && details !== "loading" ? details.cadence : null;
  const livePeriodEnd =
    details && details !== "loading"
      ? details.current_period_end
      : currentPeriodEnd;
  const liveCancelling =
    details && details !== "loading"
      ? details.cancel_at_period_end
      : cancelAtPeriodEnd;

  // Plan label.
  const planLabel = (() => {
    if (branch === "active" || branch === "cancelling") {
      if (liveCadence === "monthly") return "Pro (Monthly)";
      if (liveCadence === "annual") return "Pro (Annual)";
      return "Pro";
    }
    if (branch === "trial") return "Free trial";
    if (branch === "expired") return "Trial expired";
    return "Loading…";
  })();

  const periodEndLong = livePeriodEnd ? formatDate(livePeriodEnd, "long") : "";

  // AI usage line — shared across every branch.
  const usageLine = (() => {
    if (!aiMeter) return "Loading usage…";
    if (aiMeter.byo_key_active) return "Unlimited · using your own API key";
    if (aiMeter.limit === null) {
      return `${aiMeter.ai_calls.toLocaleString()} AI calls this month`;
    }
    return `${aiMeter.ai_calls.toLocaleString()} / ${aiMeter.limit.toLocaleString()} AI calls this month`;
  })();
  const usageRatio =
    aiMeter && aiMeter.limit && aiMeter.limit > 0 && !aiMeter.byo_key_active
      ? Math.min(1, aiMeter.ai_calls / aiMeter.limit)
      : 0;
  const usageLevel: "ok" | "warn" | "crit" =
    usageRatio >= 0.85 ? "crit" : usageRatio >= 0.6 ? "warn" : "ok";

  const upgradePair = (
    <div className="sub-plan-upgrade-row">
      <button
        type="button"
        className="btn-primary"
        onClick={() => void goToCheckout("monthly")}
        disabled={busy !== null}
      >
        {busy === "checkout-monthly"
          ? "Redirecting…"
          : "Upgrade — $9.99/month"}
      </button>
      <button
        type="button"
        className="btn-primary"
        onClick={() => void goToCheckout("annual")}
        disabled={busy !== null}
      >
        {busy === "checkout-annual"
          ? "Redirecting…"
          : "Upgrade — $89.99/year (save 25%)"}
      </button>
    </div>
  );

  return (
    <section
      className="settings-section"
      id="settings-section-subscription"
    >
      <h3>Plan &amp; Billing</h3>

      <div className="sub-plan-row">
        <div className="sub-plan-label">
          <span className="sub-plan-name">{planLabel}</span>
          {branch === "trial" && daysRemaining !== null && (
            <span className="muted sub-plan-meta">
              {daysRemaining === 0
                ? "Trial ended"
                : `${daysRemaining} ${daysRemaining === 1 ? "day" : "days"} remaining`}
            </span>
          )}
          {branch === "cancelling" && periodEndLong && (
            <span className="muted sub-plan-meta">
              Cancels on {periodEndLong}
            </span>
          )}
          {branch === "active" && periodEndLong && (
            <span className="muted sub-plan-meta">
              Renews {periodEndLong}
            </span>
          )}
          {branch === "expired" && (
            <span className="muted sub-plan-meta">
              AI features are disabled until you subscribe
            </span>
          )}
          {branch === "loading" && (
            <span className="muted sub-plan-meta">Loading plan…</span>
          )}
        </div>
      </div>

      {/* --- Active (paying, not scheduled to cancel) --- */}
      {branch === "active" && (
        <div className="sub-actions-block">
          <div className="sub-actions-row">
            {liveCadence && (
              <button
                type="button"
                onClick={() => void doSwitchPlan()}
                disabled={busy !== null}
              >
                {busy === "switch"
                  ? "Switching…"
                  : liveCadence === "monthly"
                    ? "Switch to annual (save 25%)"
                    : "Switch to monthly"}
              </button>
            )}
            <button
              type="button"
              onClick={goToPortal}
              disabled={busy !== null}
            >
              {busy === "portal" ? "Opening…" : "Manage payment method"}
            </button>
            {!confirmingCancel && (
              <button
                type="button"
                className="btn-danger"
                onClick={() => setConfirmingCancel(true)}
                disabled={busy !== null}
              >
                Cancel subscription
              </button>
            )}
          </div>
          {confirmingCancel && (
            <div className="sub-cancel-confirm">
              <p>
                Are you sure? You'll keep access until{" "}
                <strong>
                  {periodEndLong || "the end of your billing period"}
                </strong>
                .
              </p>
              <div className="sub-cancel-confirm-actions">
                <button
                  type="button"
                  onClick={() => setConfirmingCancel(false)}
                  disabled={busy !== null}
                >
                  Keep subscription
                </button>
                <button
                  type="button"
                  className="btn-danger"
                  onClick={() => void doCancel()}
                  disabled={busy !== null}
                >
                  {busy === "cancel"
                    ? "Cancelling…"
                    : "Yes, cancel subscription"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* --- Cancelling at period end --- */}
      {branch === "cancelling" && (
        <div className="sub-actions-block">
          <div className="sub-actions-row">
            <button
              type="button"
              className="btn-primary"
              onClick={() => void doReactivate()}
              disabled={busy !== null}
            >
              {busy === "reactivate"
                ? "Reactivating…"
                : "Reactivate subscription"}
            </button>
            <button
              type="button"
              onClick={goToPortal}
              disabled={busy !== null}
            >
              {busy === "portal" ? "Opening…" : "Manage payment method"}
            </button>
          </div>
          {/* Keep the cancel-confirm block hidden here — the user is
              already in the cancelling state. Switch-plan intentionally
              omitted too: plan switches on a cancelling sub get
              weird with Stripe's proration logic, and there's no
              strong use case for it. Reactivate first, then switch. */}
        </div>
      )}

      {/* --- Trial / expired branches: upgrade pair only --- */}
      {(branch === "trial" || branch === "expired") && (
        <div className="sub-actions-block">{upgradePair}</div>
      )}

      {/* --- AI usage meter — shown in every branch --- */}
      <div className={`sub-usage sub-usage-${usageLevel}`}>
        <div className="sub-usage-row">
          <span className="sub-usage-label">AI usage</span>
          <span className="sub-usage-value">{usageLine}</span>
        </div>
        {aiMeter &&
          !aiMeter.byo_key_active &&
          aiMeter.limit !== null &&
          aiMeter.limit > 0 && (
            <div className="sub-usage-bar" aria-hidden>
              <div
                className="sub-usage-bar-fill"
                style={{ width: `${Math.round(usageRatio * 100)}%` }}
              />
            </div>
          )}
        {aiMeter && !aiMeter.byo_key_active && aiMeter.reset_at && (
          <div className="sub-usage-reset muted">
            Resets {formatDate(aiMeter.reset_at, "long")}
          </div>
        )}
      </div>

      {/* --- BYOK status --- */}
      <div className="sub-byok">
        <div className="sub-byok-label">Bring your own key</div>
        {aiMeter?.byo_key_active ? (
          <div className="sub-byok-value">
            <span className="sub-byok-badge sub-byok-badge-on">Active</span>
            <span className="muted">
              {aiMeter.byo_provider && aiMeter.byo_model
                ? `${aiMeter.byo_provider} · ${aiMeter.byo_model}`
                : aiMeter.byo_provider ?? "Configured"}
            </span>
          </div>
        ) : (
          <div className="sub-byok-value muted">
            Not configured. Add a key in AI settings below to remove the
            monthly call limit.
          </div>
        )}
      </div>

      {error && <div className="form-error">{error}</div>}
    </section>
  );
}
